import { addMonths, format, getYear, startOfMonth, subMonths } from "date-fns";

export const CALENDAR_YEAR_SELECT_SPAN = 1;

/**
 * Returns previous, current and next month keys for a calendar anchor date
 */
export const getAdjacentMonthKeys = (anchorDate: Date): string[] => {
  const current = format(anchorDate, "yyyy-MM");

  return [
    format(subMonths(anchorDate, 1), "yyyy-MM"),
    current,
    format(addMonths(anchorDate, 1), "yyyy-MM"),
  ];
};

/**
 * Builds an ISO range covering a full calendar month
 */
export const getMonthQueryRange = (
  monthKey: string,
): { from: string; to: string } => {
  const [year, month] = monthKey.split("-").map(Number);
  const from = new Date(year, month - 1, 1);
  const to = addMonths(from, 1);

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
};

/**
 * Derives yyyy-MM from an ISO datetime
 */
export const getMonthKeyFromIso = (iso: string): string =>
  format(new Date(iso), "yyyy-MM");

/**
 * Filters events that overlap a calendar month, sorted by start time
 */
export const filterEventsOverlappingMonth = <
  T extends { startsAt: string; endsAt: string },
>(
  events: T[],
  monthDate: Date,
): T[] => {
  const monthKey = format(monthDate, "yyyy-MM");
  const { from, to } = getMonthQueryRange(monthKey);
  const monthStart = new Date(from);
  const monthEnd = new Date(to);

  return events
    .filter(
      (event) =>
        new Date(event.startsAt) < monthEnd &&
        new Date(event.endsAt) > monthStart,
    )
    .sort(
      (left, right) =>
        new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime(),
    );
};

/**
 * Builds ascending year options for Select (reference ± spanYears)
 */
export const buildYearSelectOptions = (
  referenceDate: Date = new Date(),
  spanYears: number = CALENDAR_YEAR_SELECT_SPAN,
): Record<string, string> => {
  const centerYear = getYear(referenceDate);
  const options: Record<string, string> = {};

  for (let offset = -spanYears; offset <= spanYears; offset += 1) {
    const year = String(centerYear + offset);
    options[year] = year;
  }

  return options;
};

/**
 * Combines year and month (1–12) into the first day of that month
 */
export const buildMonthDate = (year: number, month: number): Date =>
  startOfMonth(new Date(year, month - 1, 1));
